
import { useState } from 'react';
import { cropOptions, serviceOptions } from '../../../mocks/roiDefaults';

export interface CalculatorInputs {
  acreage: number;
  cropType: string;
  applicationsPerSeason: number;
  selectedServices: string[];
  currentMethod: string;
}

interface Props {
  onCalculate: (inputs: CalculatorInputs) => void;
}

export default function CalculatorForm({ onCalculate }: Props) {
  const [acreage, setAcreage] = useState('');
  const [cropType, setCropType] = useState('corn');
  const [applications, setApplications] = useState(2);
  const [selectedServices, setSelectedServices] = useState<string[]>(['aerial', 'timing']);
  const [currentMethod, setCurrentMethod] = useState('ground');
  const [showValidation, setShowValidation] = useState(false);

  const toggleService = (id: string) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const acres = parseFloat(acreage);
    
    // Validation: Check if inputs are valid
    if (!acres || acres <= 0 || applications <= 0 || selectedServices.length === 0) {
      setShowValidation(true);
      return;
    }
    
    setShowValidation(false);
    onCalculate({
      acreage: acres,
      cropType,
      applicationsPerSeason: applications,
      selectedServices,
      currentMethod,
    });
  };

  // Check if form is valid
  const isValid = acreage && parseFloat(acreage) > 0 && applications > 0 && selectedServices.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Validation Message */}
      {showValidation && !isValid && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
            <i className="ri-error-warning-line text-red-500 text-lg"></i>
          </div>
          <div className="text-sm text-red-700">
            <p className="font-semibold mb-1">Please complete all required fields:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              {(!acreage || parseFloat(acreage) <= 0) && <li>Enter a valid acreage amount</li>}
              {applications <= 0 && <li>Set at least 1 application per season</li>}
              {selectedServices.length === 0 && <li>Select at least one service</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Acreage */}
      <div>
        <label htmlFor="acreage" className="block text-sm font-bold mb-2 text-gray-800">
          Total Farm Acreage <span className="text-red-600">*</span>
        </label>
        <div className="relative">
          <input
            type="number"
            id="acreage"
            value={acreage}
            onChange={e => setAcreage(e.target.value)}
            placeholder="e.g. 1,200"
            min="1"
            required
            className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-lg"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">acres</span>
        </div>
      </div>

      {/* Crop Type */}
      <div>
        <label htmlFor="cropType" className="block text-sm font-bold mb-2 text-gray-800">
          Primary Crop
        </label>
        <select
          id="cropType"
          value={cropType}
          onChange={e => setCropType(e.target.value)}
          className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-700 focus:border-transparent text-sm bg-white cursor-pointer"
        >
          {cropOptions.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Current Method */}
      <div>
        <label className="block text-sm font-bold mb-3 text-gray-800">Current Spraying Method</label>
        <div className="space-y-2">
          {[
            { value: 'ground', label: 'Ground Sprayer' },
            { value: 'aerial-plane', label: 'Aerial Crop Duster' },
            { value: 'manual', label: 'Manual / Backpack' },
            { value: 'none', label: 'No Current Spraying' },
          ].map(m => (
            <label
              key={m.value}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-emerald-300 transition-colors cursor-pointer"
            >
              <input
                type="radio"
                name="currentMethod"
                value={m.value}
                checked={currentMethod === m.value}
                onChange={() => setCurrentMethod(m.value)}
                className="w-4 h-4 text-emerald-700 focus:ring-emerald-700 cursor-pointer"
              />
              <span className="text-sm text-gray-700">{m.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Applications Per Season */}
      <div>
        <label className="block text-sm font-bold mb-2 text-gray-800">
          Applications Per Season <span className="text-red-600">*</span>
        </label>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setApplications(Math.max(1, applications - 1))}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:border-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-subtract-line text-lg"></i>
          </button>
          <span className="text-2xl font-bold text-emerald-800 w-8 text-center">{applications}</span>
          <button
            type="button"
            onClick={() => setApplications(Math.min(8, applications + 1))}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:border-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Services */}
      <div>
        <label className="block text-sm font-bold mb-3 text-gray-800">
          Services to Include <span className="text-red-600">*</span>
        </label>
        <div className="space-y-2">
          {serviceOptions.map(s => (
            <label
              key={s.id}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedServices.includes(s.id)
                  ? 'border-emerald-600 bg-emerald-50/50'
                  : 'border-gray-200 hover:border-emerald-300'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedServices.includes(s.id)}
                onChange={() => toggleService(s.id)}
                className="w-4 h-4 mt-0.5 text-emerald-700 focus:ring-emerald-700 rounded cursor-pointer"
              />
              <div>
                <span className="text-sm font-semibold text-gray-800">{s.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-base tracking-wide"
      >
        Calculate My Savings
      </button>
    </form>
  );
}
